import { Button, Tooltip } from "@ondit/ui";
import { Menu, PanelLeft, X } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useSidebarResize } from "@/screens/workspace/w/components/sidebar/hooks/use-sidebar-resize";
import { SIDEBAR_WIDTH } from "@/stores/constants";
import { useSidebarStore } from "@/stores/sidebar/store";
import type { Me, Org, OrgEntity } from "~/core/api";
import { useMedia } from "~/core/hooks";
import { useT } from "~/core/i18n";
import { useWorkspaces } from "~/core/workspace";
import { BrandSwitcher } from "~/shell/brand-switcher";
import { useSidebarChrome } from "~/shell/chrome";
import { ModeSwitcher } from "~/shell/mode-switcher";
import { useSearch } from "~/shell/search";
import { UserMenu } from "~/shell/user-menu";

/** The column's own edge, eight pixels wide and centred on the rule. */
const EDGE = "absolute top-0 right-0 bottom-0 z-20 w-[8px] translate-x-1/2";

/**
 * What the edge draws while it is under the pointer or being dragged.
 *
 * The seam itself belongs to the content pane and is a hairline. This is that
 * seam answering: a second line over it, so the thing you are about to grab
 * says so. It used to be the resting state, because the column drew a border of
 * its own on top of the pane's and nothing could turn either off.
 */
const EDGE_LINE =
	"after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] after:-translate-x-1/2 after:bg-[var(--border)] after:opacity-0 after:transition-opacity hover:after:opacity-100 [[data-resizing]_&]:after:opacity-100";

/**
 * The application's left column — one of them, for both modules.
 *
 * Shared top to bottom except for the middle: the brand switcher, the mode
 * switch and the account row are the same components wherever the reader is, so
 * crossing between Analytics and the Agent does not redraw the parts of the
 * column that say where you are. What changes is the navigation between them,
 * which is the module's to describe.
 *
 * Collapse and width belong to `WorkspaceChrome`: the frame sizes the grid track
 * from the same answer, and two components each holding a copy of it disagree
 * the moment one is toggled. The resize edge is drawn here because it is the
 * column's own edge, but it writes through the chrome's store.
 */
export function Sidebar({
	me,
	org,
	brands,
	brandId,
	canAddBrand,
	onAddBrand,
	onOpenSettings,
	onOpenBrandSettings,
	children,
}: {
	me: Me;
	org: Org;
	brands: OrgEntity[];
	brandId?: string;
	canAddBrand: boolean;
	onAddBrand: () => void;
	/** The person's own settings, from the account row. */
	onOpenSettings?: () => void;
	/** The brand's, from the brand menu — the same screen, reached from the
	 *  thing it is about. */
	onOpenBrandSettings?: () => void;
	/** The active module's navigation. */
	children: ReactNode;
}) {
	const t = useT();
	const navigate = useNavigate();
	const location = useLocation();
	const workspaces = useWorkspaces();
	const { setOpen: setSearchOpen } = useSearch();
	const { isCollapsed, isPeeking } = useSidebarChrome();
	// Below the breakpoint the column is a strip above the screen and there is
	// no rail to be in — the toggle that would leave one is not even drawn. The
	// frame keeps its answer either way, so a phone held sideways puts the
	// reader back where they were.
	const narrow = useMedia("(max-width: 860px)");
	const rail = isCollapsed && !isPeeking && !narrow;
	const toggleCollapsed = useSidebarStore((state) => state.toggleCollapsed);
	const sidebarWidth = useSidebarStore((state) => state.sidebarWidth);
	const { handlePointerDown } = useSidebarResize();
	const [mobileOpen, setMobileOpen] = useState(false);
	useEffect(() => {
		if (location.pathname) setMobileOpen(false);
	}, [location.pathname]);

	// A separator is not a button, so Enter and Space do not activate it on their
	// own; the column still has to be reachable from the keyboard when it is the
	// only thing between the reader and a collapsed rail.
	const onEdgeKeyDown = useCallback(
		(event: React.KeyboardEvent) => {
			if (event.key === "Enter" || event.key === " ") {
				event.preventDefault();
				toggleCollapsed();
			}
		},
		[toggleCollapsed],
	);

	// Switching brand keeps the screen: somebody comparing two brands' gaps
	// wants the other brand's gaps, not its overview.
	const tail =
		brandId && location.pathname.startsWith(`/w/${brandId}`) ? location.pathname.slice(`/w/${brandId}`.length) : "";

	return (
		<Tooltip.Provider delayDuration={0}>
			<div className="relative h-full">
				<nav
					className={mobileOpen ? "sidebar sidebar-container is-mobile-open" : "sidebar sidebar-container"}
					/* What the rail's rules key off. The frame owns the answer and
					   publishes it as a width; the column publishes it as an attribute,
					   because "hide every label" is not a thing a width can say. */
					data-collapsed={rail || undefined}
				>
					<div className="sidebar-head">
						<BrandSwitcher
							brandId={brandId ?? ""}
							brands={brands}
							orgs={workspaces.orgs.map((one) => ({ id: one.id, name: one.name, current: one.id === org.id }))}
							collapsed={rail}
							canAddBrand={canAddBrand}
							onSelectBrand={(id) => navigate(`/w/${id}${tail}`)}
							onSelectOrg={(id) => {
								workspaces.choose(id);
								// Every path below this names an id in the workspace being
								// left, so the switch starts at the top.
								navigate("/");
							}}
							onAddBrand={onAddBrand}
							onOpenSettings={onOpenBrandSettings}
							onSearch={() => setSearchOpen(true)}
						/>
						<Button
							variant="ghost"
							size="icon"
							className="rail-toggle max-[860px]:hidden"
							onClick={toggleCollapsed}
							aria-label={t(rail ? "nav.expand" : "nav.collapse")}
						>
							<PanelLeft />
						</Button>
						<Button
							className="mobile-nav-toggle hidden max-[860px]:inline-flex"
							variant="default"
							size="icon"
							aria-expanded={mobileOpen}
							aria-controls="brand-navigation"
							aria-label={t(mobileOpen ? "nav.collapse" : "nav.expand")}
							onClick={() => setMobileOpen(!mobileOpen)}
						>
							{mobileOpen ? <X /> : <Menu />}
						</Button>
					</div>

					{brandId ? <ModeSwitcher brandId={brandId} collapsed={rail} /> : null}

					{/* The only part of the column that scrolls. The brand above it and
				    the account below it are how a person tells which workspace they
				    are in, and a scrollbar that carries those away leaves them
				    scrolling to find out where they are. */}
					<div className="nav-scroll" id="brand-navigation">
						{children}
					</div>

					<div className="sidebar-foot">
						<UserMenu
							name={me.user.name || me.user.email}
							credits={org.credits}
							collapsed={rail}
							onOpenSettings={onOpenSettings}
						/>
					</div>
				</nav>

				{/* Not on the peek card: the resize hook writes an inline
				    `--sidebar-width` that out-specifies the `[data-peek]` rule,
				    stranding the card at a stale width.

				    Two elements rather than one with a varying `role`: collapsed, the
				    edge is the only way back and is a real button; expanded, it is a
				    separator you drag and clicking it would collapse the column by
				    accident. */}
				{!isPeeking &&
					(rail ? (
						<button
							type="button"
							className={`${EDGE} ${EDGE_LINE} cursor-e-resize`}
							onClick={toggleCollapsed}
							aria-label={t("nav.expand")}
						/>
					) : (
						/* biome-ignore lint/a11y/useSemanticElements: the semantic separator
						   is `<hr>`, which cannot carry a drag handle or take focus. */
						<div
							className={`${EDGE} ${EDGE_LINE} cursor-ew-resize`}
							onPointerDown={handlePointerDown}
							onKeyDown={onEdgeKeyDown}
							role="separator"
							tabIndex={0}
							aria-orientation="vertical"
							aria-label={t("nav.resize")}
							aria-valuenow={sidebarWidth}
							aria-valuemin={SIDEBAR_WIDTH.MIN}
							aria-valuemax={SIDEBAR_WIDTH.MAX}
						/>
					))}
			</div>
		</Tooltip.Provider>
	);
}
